import React, { useState, useEffect } from 'react';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import { useFlowNavigation, useFlow } from '../../../contexts/FlowContext';
import { useMessages } from '../../../contexts/ThemeContext';
import { useSession } from '../../../contexts/SessionContext';
import LdapAuthService from '../../../services/auth/LdapAuthService';
import ContactService from '../../../services/contact/ContactService';
import SupportService from '../../../services/contact/SupportService';
import SecureSessionService from '../../../services/session/SecureSessionService';
import RateLimiter from '../../../security/RateLimiter';
import IncorrectPathModal from '../../../components/modals/IncorrectPathModal';
import SessionExpiryModal from '../../../components/modals/SessionExpiryModal';
import AccountLockedModal from '../../../components/modals/AccountLockedModal';
import ChangePasswordPage from '../changePassword/ChangePasswordPage';
import BusinessErrorMessage from '../error/BusinessErrorMessage';
import { ORIGINAL_CLIENT_REDIRECT_URL, FORGOT_PASSWORD_URL, REGISTER_URL } from '../../../config';
import { getForgotPasswordRedirectUrl, getRegisterRedirectUrl } from '../../../utils/nativeApp';
import '../../../styles/CarelonRx/login.css';

const LoginPage: React.FC = () => {
  const { nextStep, goToStep } = useFlowNavigation();
  const { setFlowData, getFlowData, completeStep, currentStep, resetFlow } = useFlow();
  const messages = useMessages();
  const { sessionData } = useSession();
  const isNativeApp = getFlowData('device') === 'native_app';

  const username = sessionData?.userId;
  const sessionId = sessionData?.sessionId ;

  const relayState = ''; 

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [sessionTimeoutError, setSessionTimeoutError] = useState(false);
  const [showAuthFailure, setShowAuthFailure] = useState(false);
  const [showIncorrectPathModal, setShowIncorrectPathModal] = useState(false);
  const [pharmacyType, setPharmacyType] = useState<'anthem-bcbs' | 'anthem-bc' | 'wellpoint'>('wellpoint');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showAccountLockedModal, setShowAccountLockedModal] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasSessionTimeout = urlParams.get('session_timeout') === 'true';
    
    if (hasSessionTimeout) {
      setSessionTimeoutError(true);
      
      urlParams.delete('session_timeout');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }

    const storedSession = SecureSessionService.getStoredSession();
    if (storedSession?.expiresAt) {
      window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: storedSession.expiresAt } }));
    }

    const authError = getFlowData('authError');
    if (authError) {
      setError(authError);
      setShowAuthFailure(true);
      setFlowData('authError', null); 
    }
  }, [getFlowData, setFlowData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordTouched(true);
    setIsAuthenticating(true);
    
    if (!username || !sessionId) {
      setError('Session expired. Please refresh the page.');
      setIsAuthenticating(false);
      return;
    }

    try {
      const rateLimitResult = RateLimiter.isAllowed(`webad_auth_${username}`, 5, 300000);

      if (!rateLimitResult.allowed) {
        setError('Too many attempts. Please wait 5 minutes before trying again.');
        setIsAuthenticating(false);
        return;
      }

      const authResult = await LdapAuthService.authenticate(username, password, sessionId);
    
      // 1. Check accountLocked field first (from API response)
      if (authResult.accountLocked) {
        setPassword('');
        setShowAccountLockedModal(true);
        setIsAuthenticating(false);
        return;
      }

      // 2. Check oktaAccountStatus for LOCKED_OUT
      if (authResult.oktaAccountStatus?.toUpperCase() === 'LOCKED_OUT') {
        setPassword('');
        setShowAccountLockedModal(true);
        setIsAuthenticating(false);
        return;
      }
      
      // 3. Check oktaAccountStatus for NOTFOUND
      if (authResult.oktaAccountStatus?.toUpperCase() === 'NOTFOUND') {
        console.error('Okta account status: NOTFOUND - User not found in Okta');
        setError('Authentication failed');
        setShowAuthFailure(true);
        setPassword('');
        setIsAuthenticating(false);
        return;
      }
      
      // 4. Check authoritativeOU for eMembers - Show incorrect OU modal
      if (authResult.authoritativeOU === 'eMembers') {
        // Map brandCd to pharmacy type for dynamic branding
        const brandCode = authResult.brandCd?.[0] || '';
        let type: 'anthem-bcbs' | 'anthem-bc' | 'wellpoint' = 'wellpoint';
        
        if (brandCode === 'ABCBS') {
          type = 'anthem-bcbs';
        } else if (brandCode === 'ABC') {
          type = 'anthem-bc';
        } else if (brandCode === 'WLP') {
          type = 'wellpoint';
        }
        
        setPharmacyType(type);
        setShowIncorrectPathModal(true);
        setIsAuthenticating(false);
        return;
      }
        
      // 5. Check forcePasswordReset - User needs to change password
      if (authResult.forcePasswordReset) {
        setPassword('');
        setFlowData('webadUsername', username);
        setFlowData('authResponse', authResult);
        setFlowData('currentPassword', password);
        setFlowData('forcePasswordChange', true);
        setFlowData('authoritativeOU', authResult.authoritativeOU);
        setFlowData('requireMFA', authResult.requireMFA);
        setIsAuthenticating(false);
        setShowChangePassword(true);
        return;
      }
      
      // 6. Check for successful authentication
      if (authResult.success) {
        setPassword('');
        setFlowData('webadUsername', username);
        setFlowData('webadAuthenticated', true);
        setFlowData('authoritativeOU', authResult.authoritativeOU);
        setFlowData('requireMFA', authResult.requireMFA);

        if (currentStep) {
          completeStep(currentStep.id);
        }

        // For rxMembers: Only call memberRegistrationDetails API if MFA is required
        if (authResult.authoritativeOU === 'rxMembers' && authResult.requireMFA && username) {
          try {
            const contactDetails = await ContactService.getMemberRegistrationDetails(
              username,
              sessionId
            );
            
            setFlowData('contactDetails', contactDetails);
            setFlowData('contactApiError', false);
            
            try {
              const supportContact = await SupportService.getSupportContactDetails(
                sessionId
              );
              
              if (supportContact) {
                setFlowData('supportContact', supportContact);
              }
            } catch (error) {
              console.error('Failed to fetch support contact during login:', error);
              setFlowData('supportContact', null);
            }
          } catch (error: any) {
            const statusCode = error?.response?.status;
            
            if (statusCode === 404) {
              console.error('Contact Details API returned 404 - User/Contact Not Found.');
              setError('Authentication failed');
              setShowAuthFailure(true);
              setPassword('');
              setIsAuthenticating(false);
              return; 
            }
            
            if (statusCode === 500 || statusCode === 503 || statusCode === 504) {
              console.error(`Contact Details API returned ${statusCode} - Server Error`);
            } else {
              if (statusCode) {
                console.error(`Contact Details API returned ${statusCode}:`, error);
              } else {
                console.error('Contact Details API failed (no HTTP status code):', error);
              }
              
              try {
                const supportContact = await SupportService.getSupportContactDetails(
                  sessionId
                );
                
                if (supportContact) {
                  setFlowData('supportContact', supportContact);
                }
              } catch (supportError) {
                console.error('Failed to fetch support contact after memberdetails error:', supportError);
                setFlowData('supportContact', null);
              }
            }
            
            setFlowData('contactApiError', true);
          }
        }

        // Route based on authoritativeOU
        if (authResult.authoritativeOU === 'rxMembers') {
          nextStep(); // Proceed to MFA (if required) or success
        } else {
          // Unknown OU
          setError('Unable to determine user access path. Please contact support.');
          setShowAuthFailure(true);
        }
        
        setIsAuthenticating(false);
        return;
      }
      
      // 7. Check success === false - Authentication failed
      if (!authResult.success) {
        console.error('Authentication failed:', authResult);
        
        if (authResult.oktaAccountStatus?.toUpperCase() === 'NOTFOUND') {
          console.error('Okta account status: NOTFOUND - User not found in Okta');
        }
        
        setError('Authentication failed');
        setShowAuthFailure(true);
        setPassword('');
        setIsAuthenticating(false);
        return;
      }

    } catch (error: any) {
      setError('An unexpected error occurred during authentication. Please try again.');
      setShowAuthFailure(true);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleBack = () => {
    SecureSessionService.clearSession();
    
    const originalUrl = relayState || ORIGINAL_CLIENT_REDIRECT_URL;
    window.location.href = originalUrl;
  };


  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError('');
    setSessionTimeoutError(false);
    setShowAuthFailure(false);
    if (passwordTouched && e.target.value.trim() === '') {
      setPasswordError('Required field.');
    } else {
      setPasswordError('');
    }
  };

  const handlePasswordBlur = () => {
    setPasswordTouched(true);
    if (password.trim() === '') {
      setPasswordError('Required field.');
    }
  };

  const isValidForm = password !== '' && !isAuthenticating;
  
  if (showChangePassword) {
    return <ChangePasswordPage />;
  }

  return (
    <PageLayout
      bannerTitle={messages.ui?.login?.title || "Log In"}

    >
      <div className="login-page-container">
        <div className="login-left-panel">
          {!showAuthFailure && (
            <div className="anthem-disclaimer">
            <div>Important Notice for Anthem and Wellpoint Members:</div>
            <p tabIndex={0}>
              Your pharmacy benefits aren't available on the CarelonRx website.
              You must log in to your health plan website to access your benefits.
            </p>
          </div>
          )}

          {!isNativeApp && (
            <a className="back-link" onClick={handleBack}>
              <img src="/assets/carelonRx/images/Keyboard Arrow Left.svg" alt="" />
              <span>Back to Log In</span>
            </a>
          )}

          <h1>Welcome</h1>
          <p className="login-subtitle">Please enter your password.</p>

          {sessionTimeoutError && (
            <div className="session-timeout-error">
              For security reasons, you have been logged out due to inactivity.
            </div>
          )}

          <form id="login-form" onSubmit={handleSubmit} className="login-form">
            <div className="password-input-group">
              <label htmlFor="password" className={passwordError ? 'error' : ''}>Password</label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  placeholder="Enter password"
                  disabled={isAuthenticating}
                  autoComplete="current-password"
                  className={`crx-input ${passwordError ? 'error' : ''}`}
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <img src={showPassword ? "/assets/carelonRx/images/Visibility Off.svg" : "/assets/carelonRx/images/Visibility.svg"} alt="" />
                </button>
              </div>
              {passwordError && (
                <div className="crx-error-container">
                  <img src="/assets/carelonRx/images/info_error.svg" alt="" className="error-icon" />
                  <span id="password-error" className="crx-error-text">{passwordError}</span>
                </div>
              )}
            </div>

            <input
              type="submit"
              className="crx-button crx-button-primary"
              value={isAuthenticating ? 'Logging in...' : 'Log In'}
              disabled={!isValidForm || !!error}
              data-type="save"
              name="Log In"
            />
          </form>

          <div className="login-actions">

            <div className="line-divider"></div>

            <a
              href={isNativeApp ? getForgotPasswordRedirectUrl() : FORGOT_PASSWORD_URL}
              className="forgot-password-link"
              {...(!isNativeApp && { target: '_blank', rel: 'noopener noreferrer' })}
            >
              Forgot Username or Password?
            </a>
            <div className="register-section">
              <span className="register-text">Not signed up? </span>
              <a
                href={isNativeApp ? getRegisterRedirectUrl() : REGISTER_URL}
                className="register-link"
                {...(!isNativeApp && { target: '_blank', rel: 'noopener noreferrer' })}
              >
                Register Now.
              </a>
            </div>
          </div>

          <BusinessErrorMessage isVisible={!!error} />
        </div>

        <IncorrectPathModal 
          isOpen={showIncorrectPathModal} 
          onClose={() => setShowIncorrectPathModal(false)}
          pharmacyType={pharmacyType}
        />

        <AccountLockedModal
          isOpen={showAccountLockedModal}
          onClose={() => setShowAccountLockedModal(false)}
        />

        {showAuthFailure && (
          <div className="login-right-panel">
            <div className="anthem-disclaimer" id="anthem-disclaimer-right-panel">
              <strong tabIndex={0}>Important Notice for Anthem and Wellpoint Members:</strong>
              <p tabIndex={0}>
                Your pharmacy benefits aren't available on the CarelonRx website. Use one of the
                following links to log in to your health plan's website.
              </p>
            </div>
            
            <div className="brand_section">
              <div className="redirection_logo">
                <img className="logo_ABCBS" src="/assets/carelonRx/images/ABCBS.png" alt="Anthem Blue Cross Blue Shield" />
              </div>
              <div className="redirection-anthem-section">
                <p tabIndex={0} className="redirection_anthem_text">
                  If you have an <strong>Anthem Blue Cross</strong> and <strong>Blue Shield</strong> plan, 
                  please visit their website to log in.
                </p>
                <a href="https://www.anthem.com/account-login/">
                  <button className="anthem_redirection_button">Continue</button>
                </a>
              </div>
            </div>

            <div className="brand_section">
              <div className="redirection_logo">
                <img className="logo_ABC" src="/assets/carelonRx/images/ABC.png" alt="Anthem Blue Cross" />
              </div>
              <div className="redirection-anthem-section">
                <p tabIndex={0} className="redirection_anthem_text">
                  If you have an <strong>Anthem Blue Cross</strong> plan, please visit their website to log in.
                </p>
                <a href="https://www.anthem.com/ca/account-login/">
                  <button className="anthem_redirection_button">Continue</button>
                </a>
              </div>
            </div>

            <div className="brand_section">
              <div className="redirection_logo">
                <img className="logo_ABC" src="/assets/carelonRx/images/Logo_Wellpoint_RGB_100pct.png" alt="Wellpoint" />
              </div>
              <div className="redirection-anthem-section">
                <p tabIndex={0} className="redirection_anthem_text">
                  If you have a <strong>Wellpoint</strong> plan, please visit their website to register to log in.
                </p>
                <a href="https://member.wellpoint.com/public/login">
                  <button className="anthem_redirection_button">Continue</button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      <SessionExpiryModal />
    </PageLayout>
  );
};

export default LoginPage;
